import {
  Button,
  Code,
  Container,
  HStack,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FileUploaderMinimal } from "@uploadcare/react-uploader/next";
import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Field } from "../components/chakra/field";
import { toaster } from "../components/chakra/toaster";
import { SectionHeader } from "../components/section-header";
import { useAppDispatch, useLoading, useToaster, useWorkspace } from "../hooks";
import { SettingsLayout } from "../layouts/settings";
import { updateWorkspace } from "../store/slices/workspace";
import "./general-settings.css";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  slug: z.string().min(1, "Slug is required"),
  vanity: z.string().min(1, "Vanity is required"),
  avatarUrl: z.url().nullable().optional(),
  bannerUrl: z.url().nullable().optional(),
});
type FormData = z.infer<typeof formSchema>;

const GeneralSettingsPage = () => {
  const workspace = useWorkspace()!;
  const dispatch = useAppDispatch();
  const [isLoading, withLoading] = useLoading();
  const withToaster = useToaster();

  const {
    handleSubmit,
    formState: { errors },
    register,
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: workspace.name,
      slug: workspace.slug,
      vanity: workspace.vanity || "",
      avatarUrl: workspace["avatar-url"],
      bannerUrl: workspace["banner-url"],
    },
  });

  const onSubmit = handleSubmit((data) => {
    withLoading(async () => {
      await withToaster(async () => {
        await dispatch(
          updateWorkspace({
            ...workspace,
            name: data.name,
            slug: data.slug,
            vanity: data.vanity,
            "avatar-url": data.avatarUrl || null,
            "banner-url": data.bannerUrl || null,
          }),
        ).unwrap();
        toaster.create({
          type: "success",
          title: "Workspace updated",
        });
      });
    });
  });

  return (
    <SettingsLayout>
      <Container pt="8" maxW="4xl">
        <Stack>
          <SectionHeader
            title="General Settings"
            subtitle="Manage your workspace's settings."
          />
          <form onSubmit={onSubmit}>
            <Stack gap="6">
              <Field
                label="Workspace Name"
                invalid={!!errors.name}
                errorText={errors.name?.message}
              >
                <Input {...register("name")} />
              </Field>
              <HStack align="top">
                <Field
                  label="Workspace Slug"
                  invalid={!!errors.slug}
                  errorText={errors.slug?.message}
                  helperText={
                    <Text>
                      Should match your Patreon URL (
                      <Code colorPalette="gray" fontSize="2xs">
                        {"https://patreon.com/c/{slug}"}
                      </Code>
                      ).
                    </Text>
                  }
                >
                  <Input {...register("slug")} />
                </Field>
                <Field
                  label="Vanity"
                  invalid={!!errors.vanity}
                  errorText={errors.vanity?.message}
                  helperText={<Text>Displayed to patrons on your page.</Text>}
                >
                  <Input {...register("vanity")} />
                </Field>
              </HStack>
              <HStack>
                <Field
                  label="Avatar URL"
                  invalid={!!errors.avatarUrl}
                  errorText={errors.avatarUrl?.message}
                >
                  <FileUploaderMinimal
                    multiple={false}
                    classNameUploader="uc-light"
                    className="general-settings__avatar-uploader"
                    onChange={(state) => {
                      setValue(
                        "avatarUrl",
                        state.successEntries.length > 0
                          ? state.successEntries[0].cdnUrl
                          : null,
                      );
                    }}
                    pubkey="94b3a8886cbe6c8c17d4"
                    sourceList="local, camera, facebook, gdrive"
                    removeCopyright
                  />
                  <Text colorPalette="fg.muted" fontSize="sm">
                    Recommended size: 240x240px
                  </Text>
                </Field>
                <Field
                  label="Banner URL"
                  invalid={!!errors.bannerUrl}
                  errorText={errors.bannerUrl?.message}
                >
                  <FileUploaderMinimal
                    multiple={false}
                    classNameUploader="uc-light"
                    className="general-settings__banner-uploader"
                    onChange={(state) => {
                      setValue(
                        "bannerUrl",
                        state.successEntries.length > 0
                          ? state.successEntries[0].cdnUrl
                          : null,
                      );
                    }}
                    pubkey="94b3a8886cbe6c8c17d4"
                    sourceList="local, camera, facebook, gdrive"
                    removeCopyright
                  />
                  <Text colorPalette="fg.muted" fontSize="sm">
                    Recommended size: 2400x600px
                  </Text>
                </Field>
              </HStack>
              <Button type="submit" loading={isLoading} colorPalette="primary">
                Save Changes
              </Button>
            </Stack>
          </form>
        </Stack>
      </Container>
    </SettingsLayout>
  );
};

export { GeneralSettingsPage };
